/**
 * La forme d'un checkpoint qui s'ouvre, pendant que Sam prépare sa première phrase : sa bulle à
 * gauche, sous sa tête, et ses réponses proposées dans le pied, en cartes.
 *
 * Il garde la géométrie de ce qui arrive — la place de celui qui parle (`SPEAKER_AVATAR_SIZE`), une
 * bulle de deux lignes (`MESSAGE_BUBBLE_PADDING_Y`, `MESSAGE_BUBBLE_TEXT_SIZE`), la carte d'une
 * réponse (`p="3"`, une ligne en `text-md`) et la colonne du fil (`CONVERSATION_MAX_WIDTH`) — et se
 * pose sous un `<DelayedSkeleton>` : c'est `<CheckpointConversation loading>` qui le monte, à la place
 * du fil et du pied.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Skeleton } from "../../atoms/Skeleton"
import { textLineBox } from "../../atoms/Skeleton/Skeleton"
import { Card } from "../../molecules/Card"
import { MESSAGE_BUBBLE_PADDING_Y, MESSAGE_BUBBLE_TEXT_SIZE } from "../../molecules/MessageBubble"
import { MESSAGE_ROW_MAX_WIDTH, SPEAKER_AVATAR_SIZE } from "../../molecules/MessageRow"
import { spacing } from "../../theme/tokens/spacing"
import { CONVERSATION_MAX_WIDTH } from "../ConversationThread"

const LINE = textLineBox(MESSAGE_BUBBLE_TEXT_SIZE)
/** La première phrase de Sam : deux lignes, le cas courant. */
const BUBBLE_HEIGHT = 2 * spacing[MESSAGE_BUBBLE_PADDING_Y] + 2 * LINE
/** Deux réponses proposées, le moins qu'il en propose. */
const REPLY_WIDTHS = [180, 140]

export type CheckpointConversationSkeletonProps = {
    /** Ce que le pied dégage de l'indicateur d'accueil (0 dans une page qui pose déjà sa zone sûre). */
    bottomPadding: number
}

export function CheckpointConversationSkeleton(props: CheckpointConversationSkeletonProps) {
    const { bottomPadding } = props

    return (
        <View style={styles.root}>
            <View style={styles.thread}>
                <View style={styles.column}>
                    <Row gap="2" align="end">
                        <Skeleton shape="circle" size={SPEAKER_AVATAR_SIZE} />
                        <View style={styles.bubble}>
                            <Skeleton shape="block" width={260} height={BUBBLE_HEIGHT} radius="lg" />
                        </View>
                    </Row>
                </View>
            </View>
            <View style={styles.footer(bottomPadding)}>
                <View style={[styles.column, styles.replies]}>
                    {REPLY_WIDTHS.map((width) => (
                        <Card key={width} p="3">
                            <Skeleton textSize="text-md" width={width} />
                        </Card>
                    ))}
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    /** Les retraits du fil (`<ConversationThread>`). */
    thread: {
        flex: 1,
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["4"],
    },
    column: {
        width: "100%",
        maxWidth: CONVERSATION_MAX_WIDTH,
        alignSelf: "center",
    },
    bubble: { flexShrink: 1, maxWidth: MESSAGE_ROW_MAX_WIDTH },
    /** Les retraits du pied de `<CheckpointConversation>`. */
    footer: (bottomPadding: number) => ({
        paddingHorizontal: theme.spacing["4"],
        paddingTop: theme.spacing["2"],
        paddingBottom: Math.max(bottomPadding, theme.spacing["3"]),
    }),
    replies: { gap: theme.spacing["2"] },
}))
